'use client';

import React, { FC, useState, useMemo, useCallback } from 'react';
import { ConfigFacet } from 'shared/types/query/FacetsConfiguration';
import { FilterTypes } from 'shared/types/query/Filter';
import { FacetFilter } from 'shared/types/query/ProductQuery';
import { cn } from 'helpers/utils/classnames';
import CheckedIcon from 'icons/checked-icon';
import ChevronDownIcon from 'icons/chevron-down';
import { gtmTrackEvent } from '../../../../../helpers/utils/gtm-events/gtmTrackEvent';

interface Facet {
  name: string;
  buckets: Bucket[];
}

interface Bucket {
  key: string;
  count: number;
}

interface ProductListFiltersProps {
  className?: string;
  facets: (Facet & ConfigFacet)[];
  onFilterChange: (filter: FacetFilter) => void;
  activeFacets: FacetFilter[];
}

const ProductListFilters: FC<ProductListFiltersProps> = ({ facets, onFilterChange, activeFacets, className }) => {
  const [expandedFacets, setExpandedFacets] = useState<Record<string, boolean>>({});
  const [showMore, setShowMore] = useState<Record<string, boolean>>({});

  const handleOnFilterChange = (bucket: Bucket, facet: Facet & ConfigFacet) => {
    const isApplyingFilter = !activeFacets.some(
      (activeFacet) => activeFacet.key === facet.distinct?.field && activeFacet.facetData.terms?.includes(bucket.key),
    );

    if (isApplyingFilter) {
      gtmTrackEvent('apply_filter', {
        filter_type: facet.name,
        selected_option: bucket.key,
      });
    }

    if (facet.distinct) {
      const urlKey = facet.distinct?.field?.split('.')?.pop();

      const facetType = facet.distinct.fieldType as FilterTypes;

      const filter: FacetFilter = {
        key: facet.distinct.field,
        urlKey: urlKey ?? '',
        facetData: {
          facetType: facetType,
          terms: [bucket.key],
        },
      };
      onFilterChange(filter);
    }
  };

  const FacetValues: FC<{
    facet: Facet & ConfigFacet;
  }> = ({ facet }) => {
    const isReadyToShip = facet.name === 'Ready to Ship' || facet.distinct?.field === 'variants.availability.isOnStock';

    const values = useMemo(() => {
      let buckets = facet.buckets;

      // For Ready to Ship, filter to only show the "true" bucket
      if (isReadyToShip) {
        buckets = facet.buckets?.filter((bucket) => String(bucket.key) === 'true');
      }

      // Apply show more/less logic
      if (facet.name === 'Color' || showMore[facet.name]) {
        return buckets;
      }

      return buckets?.slice(0, 5);
    }, [facet, isReadyToShip]);

    const handleCheckboxChange = React.useCallback(
      (bucket: Bucket) => {
        handleOnFilterChange(bucket, facet);
      },
      [facet],
    );

    return (
      <div className="flex flex-col gap-2 pb-5" aria-labelledby={`facet-${facet.name}`}>
        {facet.name === 'Color' ? (
          <div className="flex flex-wrap gap-3">
            {values?.map((bucket: Bucket) => (
              <button
                type="button"
                key={bucket.key}
                onClick={() => handleCheckboxChange(bucket)}
                className={cn(
                  'flex cursor-pointer rounded-full p-[3px]',
                  activeFacets.some(
                    (activeFacet) =>
                      activeFacet.key === facet.distinct?.field && activeFacet.facetData.terms?.includes(bucket.key),
                  ) && 'border border-primary-black',
                )}
                aria-label={`Filter by ${bucket.key}`}
              >
                <input
                  onChange={(e) => handleCheckboxChange(bucket)}
                  onClick={(e) => e.stopPropagation()}
                  title={bucket?.key || ''}
                  type="checkbox"
                  checked={activeFacets.some(
                    (activeFacet) =>
                      activeFacet.key === facet.distinct?.field && activeFacet.facetData.terms?.includes(bucket.key),
                  )}
                  className="absolute size-6 cursor-pointer opacity-0"
                />
                <div
                  className={`linear-${bucket?.key?.toLowerCase()} flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full`}
                >
                  {activeFacets.some(
                    (activeFacet) =>
                      activeFacet.key === facet.distinct?.field && activeFacet.facetData.terms?.includes(bucket.key),
                  ) && <CheckedIcon className="absolute cursor-pointer" />}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {values?.map((bucket: Bucket) => (
              <label
                key={bucket.key}
                htmlFor={`filter-${bucket.key}`}
                className="flex cursor-pointer items-start gap-2 align-top text-sm font-normal text-primary-black"
              >
                <input
                  id={`filter-${bucket.key}`}
                  type="checkbox"
                  className="mt-px flex size-4 rounded-sm border-gray-500"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleCheckboxChange(bucket)}
                  checked={activeFacets.some(
                    (activeFacet) =>
                      activeFacet.key === facet.distinct?.field && activeFacet.facetData.terms?.includes(bucket.key),
                  )}
                />
                <span className={`text-sm font-normal text-primary-black`}>
                  {/* For Ready to Ship, show custom label without the "true" text */}
                  {isReadyToShip ? `${facet.name} (${bucket.count})` : `${bucket.key} (${bucket.count})`}
                </span>
              </label>
            ))}
            {!isReadyToShip && facet.buckets?.length > 5 && (
              <div
                onClick={() => setShowMore((prev) => ({ ...prev, [facet.name]: !prev[facet.name] }))}
                className="cursor-pointer text-end text-sm font-normal text-primary-black"
                role="button"
                aria-expanded={showMore[facet.name]}
                aria-controls={`facet-${facet.name}-values`}
              >
                {showMore[facet.name] ? 'Show less' : 'Show more'}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const Facet: FC<{ facet: Facet & ConfigFacet }> = ({ facet }) => {
    const isExpanded = expandedFacets[facet.name] ?? !facet.collapse;

    const toggleExpand = useCallback(
      (event: any) => {
        if (event.type == 'click' || event.key == 'Enter') {
          event.preventDefault();
          gtmTrackEvent('plp_interactions', { click_action: 'facet toggle' });
          setExpandedFacets((prev) => ({ ...prev, [facet.name]: !prev[facet.name] }));
        }
      },
      [facet.name],
    );

    const handleMouseDown = useCallback((event: React.MouseEvent) => {
      event.preventDefault();
    }, []);

    return (
      <div key={'facet-' + facet.name} className="border-b border-gray-300 last:border-b-0">
        {facet.showHeading ? (
          <>
            <div
              onClick={toggleExpand}
              onMouseDown={handleMouseDown}
              className="flex w-full items-center justify-between py-5 text-left"
              aria-expanded={isExpanded}
              id={`facet-${facet.name}`}
              role="button"
              tabIndex={0}
              onKeyDown={toggleExpand}
            >
              <span className="text-sm font-semibold">{facet.name}</span>
              <ChevronDownIcon className={cn('h-5 w-5 fill-primary-gray', { 'rotate-180': isExpanded })} />
            </div>
            {isExpanded && <FacetValues facet={facet} />}
          </>
        ) : (
          <div className="pt-5">
            <FacetValues facet={facet} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-auto border border-gray-300 bg-white px-4 lg:h-auto lg:rounded-md',
        className,
      )}
    >
      {facets?.map((facet) => facet.buckets?.length > 0 && <Facet key={facet.name} facet={facet} />)}
    </div>
  );
};

export default ProductListFilters;
